import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { TrendIndicator } from "../TrendIndicator";
import { IngredientsAnalyticsResponse, IngredientsSortField, PAGE_SIZE_OPTIONS, WeightUnit } from "../types";

const TREND_TOOLTIP =
  "Trend compares the selected period to the previous matching period (e.g. This month vs. Last month).";

// The API returns grams; the lb/kg toggle is display-only.
const GRAMS_PER_UNIT: Record<WeightUnit, number> = { kg: 1000, lb: 453.59237 };
const formatWeight = (grams: number, unit: WeightUnit): string => {
  const value = grams / GRAMS_PER_UNIT[unit];
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unit}`;
};

interface Column {
  field: IngredientsSortField;
  label: string;
  info?: boolean;
}

const COLUMNS: Column[] = [
  { field: "rank", label: "Rank" },
  { field: "name", label: "Ingredient" },
  { field: "totalGramsServed", label: "Total Served" },
  { field: "trend", label: "Trend", info: true },
];

interface IngredientsTableProps {
  data: IngredientsAnalyticsResponse | null;
  loading: boolean;
  error: boolean;
  unit: WeightUnit;
  sortBy: IngredientsSortField;
  sortDir: "asc" | "desc";
  page: number;
  pageSize: number;
  onSort: (field: IngredientsSortField) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const IngredientsTable = ({
  data,
  loading,
  error,
  unit,
  sortBy,
  sortDir,
  page,
  pageSize,
  onSort,
  onPageChange,
  onPageSizeChange,
}: IngredientsTableProps) => {
  const rows = data?.items ?? [];
  const totalCount = data?.meta.totalCount ?? 0;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.field} align="left" sortDirection={sortBy === col.field ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === col.field}
                    direction={sortBy === col.field ? sortDir : "desc"}
                    onClick={() => onSort(col.field)}
                  >
                    {col.label}
                    {col.info && (
                      <Tooltip title={TREND_TOOLTIP}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.5, color: "text.secondary" }} />
                      </Tooltip>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 &&
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {COLUMNS.map(col => (
                    <TableCell key={col.field} align="left">
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && error && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  Unable to load ingredients. Please refresh.
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No ingredients match the selected filters.
                </TableCell>
              </TableRow>
            )}

            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.rank}</TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Tooltip title={row.name}>
                    <Typography variant="body2" noWrap>
                      {row.name}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatWeight(row.totalGramsServed, unit)}</TableCell>
                <TableCell>
                  <TrendIndicator trend={row.trendPercent} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        count={totalCount}
        rowsPerPage={pageSize}
        page={page - 1}
        onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
        onRowsPerPageChange={e => onPageSizeChange(Number(e.target.value))}
        labelRowsPerPage="Results per page"
      />
    </>
  );
};
